
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  getCurrentInstance,
  type PropType,
} from 'vue';
import Params from '../Params.vue';
import ApiRequestReturnParams from '../ApiRequestReturnParams.vue';
import Returns from '../Returns.vue';
import StatusBadge from '@/components/ui/StatusBadge.vue';
import PlatformSpecification from '../PlatformSpecification.vue';
import Signature from '../signature/Signature';
import CodeExample from '@/components/docs-main/code-rails-view/CodeExampleBody.vue';
import { BadgesType, type referencesMethod } from '@/templates/References';
import { convertToHtmlHelper, getSelectedLangName } from '@/heplers';
import type { LanguagesPack, SelectedLanguagesPack } from '@/store/types';
import { getLastNameHelper } from '@/heplers/stringHelpers';
import { useCurrentCodeBlock } from '@/heplers/composables';
import CodeBlockAnchor from '@/components/docs-main/code-rails-view/CodeBlockAnchor.vue';
import { MethodHeader, type MethodHeaderProp } from '@/components/docs-main/reference-pages/method';
import { getBadgeKeys } from '@/components/docs-main/reference-pages/badge';

interface GrpcObject {
  grpc?: string;
  [key: string]: string | undefined;
}
export default defineComponent({
  name: 'Method',
  components: {
    MethodHeader,
    CodeBlockAnchor,
    Params,
    Returns,
    ApiRequestReturnParams,
    StatusBadge,
    PlatformSpecification,
    Signature,
    CodeExample,
  },
  props: {
    method: {
      type: Object as PropType<referencesMethod>,
      required: true,
    },
  },
  setup(props) {
    const instance = getCurrentInstance();
    const store = computed(() => instance?.proxy?.$store);
    const route = computed(() => instance?.proxy?.$route);

    const { setCurrentCodeId } = useCurrentCodeBlock();

    const content = ref<HTMLElement | null>(null);

    const languagesPack = computed<SelectedLanguagesPack>(
      () => (store.value?.getters?.getSelectedLanguagesPack as SelectedLanguagesPack) ?? ({} as SelectedLanguagesPack)
    );

    const contentData = computed(() => store.value?.getters?.getContent);

    const methodAnchorId = computed(() => getLastNameHelper(props.method?.fqdn ?? ''));

    /**
     * Собираем объект для alert блоков
     */
    const statusBadges = computed(() => {
      if (!props.method.attributes) return {};

      const allowedBadgeNames: BadgesType[] = Object.values(BadgesType);

      const filter = (
        attributes: Record<string, unknown>,
        allowed: BadgesType[]
      ): Record<string, string> =>
        allowed.reduce<Record<string, string>>((acc, name) => {
          if (attributes[name] && typeof attributes[name] === 'string') {
            acc[name] = attributes[name] as string;
          }
          return acc;
        }, {});

      return filter(props.method.attributes as Record<string, unknown>, allowedBadgeNames);
    });

    const preparedMethodHeader = computed<MethodHeaderProp>(() => {
      const { title, kind, attributes } = props.method;
      return {
        title: getTitle(title),
        attributes: getBadgeKeys(props.method),
        kind: kind as string,
        version: attributes?.since,
        link: methodAnchorId.value
      };
    })

    const isUnitySdkEvents = computed(
      () =>
        props.method?.fqdn?.split('.')[1] === 'unitysdk' && props.method?.kind === 'events'
    );

    // Language key from route; normalize c# to csharp for display
    const currentLanguage = computed(() => {
      const langName = getSelectedLangName(route.value?.meta?.fqdn as string) as keyof LanguagesPack;
      const packValue = languagesPack.value[langName];
      return packValue === 'c#' ? 'csharp' : (packValue ?? '');
    });

    const isManagementApi = computed(() => {
      const root = contentData.value?.root_fqdn;
      return (
        root === 'references.httpapi' &&
        root !== 'references.httpapi.errors'
      );
    });

    /**
     * Фильтр, для методов не доступных в рамках текущего языка
     */
    const checkAllowedForCurrentLanguage = computed(() => {
      if (props.method?.attributes?.[currentLanguage.value] === false) {
        return false;
      }
      return true;
    });

    const convertToHtml = (mdString: string) => {
      if (!mdString) return '';
      return convertToHtmlHelper(mdString);
    }

    const getTitle = (title: string | GrpcObject | undefined): string => {
      if (typeof title === 'object' && title?.grpc) {
        return title.grpc;
      }
      return (title as string) ?? '';
    }

    onMounted(() => {
      // Prism refers to a UMD global
      if (content.value && typeof (window as unknown as { Prism?: { highlightAllUnder: (el: HTMLElement) => void } }).Prism !== 'undefined') {
        (window as unknown as { Prism: { highlightAllUnder: (el: HTMLElement) => void } }).Prism.highlightAllUnder(content.value);
      }
    });

    return {
      content,
      methodAnchorId,
      statusBadges,
      isUnitySdkEvents,
      currentLanguage,
      isManagementApi,
      checkAllowedForCurrentLanguage,
      preparedMethodHeader,
      convertToHtml,
      getTitle,
      setCurrentCodeId
    };
  },
});
